import * as React from 'react';
import {
  Body1Strong,
  Card,
  CardFooter,
  CardHeader,
  CardPreview,
  Image,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { defineMessage, useIntl } from 'react-intl';
import { TextLink } from '@foundershub/components';
import aboutImage from '../media/waiting-room-about-ms-for-startups.webp';
import connectImage from '../media/waiting-room-connect-community.webp';
import discoverImage from '../media/waiting-room-discover.webp';
import getStartedImage from '../media/waiting-room-get-started.webp';
import { breakpoints } from '../modules/breakpoints';

export function WelcomePageCards() {
  const intl = useIntl();

  return (
    <>
      <LearnMoreCard
        title={intl.formatMessage(aboutMicrosoftForStartupsTitle)}
        imageAltText={intl.formatMessage(aboutMicrosoftForStartupsImageAltText)}
        content={intl.formatMessage(aboutMicrosoftForStartupsMessage)}
        image={aboutImage}
        link="https://www.microsoft.com/startups"
        linkText={intl.formatMessage(aboutMicrosoftForStartupsLearnMoreLink)}
      />

      <LearnMoreCard
        title={intl.formatMessage(discoverTitle)}
        imageAltText={intl.formatMessage(discoverImageAltText)}
        content={intl.formatMessage(discoverMessage)}
        image={discoverImage}
        link="https://startups.microsoft.com/blog"
        linkText={intl.formatMessage(discoverLearnMoreLink)}
      />

      <LearnMoreCard
        title={intl.formatMessage(connectTitle)}
        imageAltText={intl.formatMessage(connectImageAltText)}
        content={intl.formatMessage(connectMessage)}
        image={connectImage}
        link="https://www.linkedin.com/company/19043535/"
        linkText={intl.formatMessage(connectLearnMoreLink)}
      />

      <LearnMoreCard
        title={intl.formatMessage(getStartedTitle)}
        imageAltText={intl.formatMessage(getStartedImageAltText)}
        content={intl.formatMessage(getStartedMessage)}
        image={getStartedImage}
        link="https://startups.microsoft.com/blog/innovate-faster-with-generative-ai-on-azure-openai-service/"
        linkText={intl.formatMessage(getStartedLearnMoreLink)}
      />
    </>
  );
}

interface LearnMoreCardProps {
  title: string;
  content: string;
  image: string;
  link: string;
  linkText: string;
  imageAltText: string;
}

function LearnMoreCard({ title, content, image, link, linkText, imageAltText }: LearnMoreCardProps) {
  const classes = useStyles();

  return (
    <Card className={classes.item}>
      <CardPreview className={classes.cardPreview}>
        <Image src={image} alt={imageAltText} fit="contain" />
      </CardPreview>
      <div className={classes.cardBody}>
        <CardHeader header={<Body1Strong as="strong">{title}</Body1Strong>} description={content} />
        <CardFooter className={classes.cardFooter}>
          <TextLink openNewTab href={link}>
            {linkText}
          </TextLink>
        </CardFooter>
      </div>
    </Card>
  );
}

const aboutMicrosoftForStartupsTitle = defineMessage({
  defaultMessage: 'About Microsoft for Startups',
  description: 'about Microsoft for Startups title',
});

const aboutMicrosoftForStartupsImageAltText = defineMessage({
  defaultMessage: 'Three people collaborating and laughing while gathered around a table with a tablet on it.',
  description: 'about Microsoft for Startups image alt text',
});

const aboutMicrosoftForStartupsMessage = defineMessage({
  defaultMessage:
    'Microsoft for Startups helps founders accelerate innovation with free access to AI tools, including OpenAI GPT-4, Azure credits, 1:1 support from Microsoft experts, and more.',
  description: 'about Microsoft for Startups message',
});

const aboutMicrosoftForStartupsLearnMoreLink = defineMessage({
  defaultMessage: 'Learn more',
  description: 'about Microsoft for Startups link label',
});

const discoverTitle = defineMessage({
  defaultMessage: 'Discover how other startups build with us',
  description: 'discover title',
});

const discoverImageAltText = defineMessage({
  defaultMessage: 'Two people walking and having a conversation.',
  description: 'discover image alt text',
});

const discoverMessage = defineMessage({
  defaultMessage:
    'See how startups are using our industry-leading AI innovation to add customer value, improve startup productivity, and lower costs.',
  description: 'discover message',
});

const discoverLearnMoreLink = defineMessage({
  defaultMessage: 'Learn more',
  description: 'discover link label',
});

const connectTitle = defineMessage({
  defaultMessage: 'Connect with our startup community ',
  description: 'connect title',
});

const connectImageAltText = defineMessage({
  defaultMessage: 'A person in an orange sweater in front of a vibrant purple background.',
  description: 'connect image alt text',
});

const connectMessage = defineMessage({
  defaultMessage:
    'Follow us on LinkedIn to stay up to speed with the latest happenings, connect with other founders, and learn from articles, videos, and more.',
  description: 'connect message',
});

const connectLearnMoreLink = defineMessage({
  defaultMessage: 'Learn more',
  description: 'connect link label',
});

const getStartedTitle = defineMessage({
  defaultMessage: 'Get started with AI',
  description: 'get started title',
});

const getStartedImageAltText = defineMessage({
  defaultMessage: 'A person pointing to something to another person on a computer monitor.',
  description: 'get started image alt text',
});

const getStartedMessage = defineMessage({
  defaultMessage:
    'Supercharge your journey to innovation with our cutting-edge AI tools, expert guidance to implement them, and cloud resources.',
  description: 'get started message',
});

const getStartedLearnMoreLink = defineMessage({
  defaultMessage: 'Learn more',
  description: 'get started link label',
});

const useStyles = makeStyles({
  item: {
    flexBasis: 'calc(50% - 12px)', // subtract half of the gap from the flex-basis
    ...breakpoints.m({ flexBasis: '100%' }),
    ...shorthands.gap(tokens.spacingVerticalL),
    ...shorthands.padding(tokens.spacingVerticalL),
  },
  cardPreview: {
    width: '100%',
    alignSelf: 'center',
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalNone, tokens.spacingVerticalNone),
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  cardFooter: {
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalNone, tokens.spacingVerticalNone),
  },
});
